import React, { useEffect, useRef, useState } from 'react';
import type { QuestLog } from '../../../shared/CharacterTypes';
import { QUEST_DATABASE } from '../../../shared/content/generated/quests.gen';
import { questCardView } from '../npc/hydrateNpcDialogue';
import { QuestGuidance } from '../Systems/NPC/guidance/QuestGuidance';
import { readPlayerWhereabouts } from '../Systems/NPC/guidance/playerWhereabouts';
import { npcPanel } from '../Systems/NPC/npcPresentation';
import { COLORS, FONT_SIZE } from '../theme';
import { QuestTrackerAtlasLinks } from './QuestTrackerAtlasLinks';
import { questTrackerGuidanceEscape } from './questTrackerGuidanceEscape';

/** Quests with compiled guidance get a "where to go" pin in the tracker (plan 02). */
export function questHasGuidance(questId: string): boolean {
  return Boolean(QUEST_DATABASE[questId]?.guidance);
}

/**
 * A small map-pin toggle beside a tracked quest's name. It opens "where to go", the local maps of
 * the quest's areas; the World Atlas is the separate line at the panel's foot (Tome UI audit:
 * one meaning per action).
 */
export const QuestMapButton: React.FC<{
  questId: string; questName: string; open: boolean;
  /** `byPointer`: a mouse or touch click (`detail` > 0), not a keyboard press. */
  onToggle: (byPointer: boolean) => void;
}> = ({ questId, questName, open, onToggle }) => (
  <button type="button" aria-label={`Where to go for ${questName}`} aria-pressed={open}
    title={open ? 'Hide where to go' : 'Where to go'} onClick={e => onToggle(e.detail > 0)}
    data-testid="quest-tracker-map-button" data-quest-map-button={questId}
    style={{
      flex: 'none', display: 'grid', placeItems: 'center', width: 24, height: 22, padding: 0,
      borderRadius: 6, cursor: 'pointer',
      border: `1px solid ${open ? COLORS.brandAccent : COLORS.borderSubtle}`,
      background: open ? 'rgba(255, 215, 0, 0.14)' : 'transparent',
      color: open ? COLORS.brandAccent : COLORS.textSecondary,
    }}>
    <svg aria-hidden width="12" height="14" viewBox="0 0 12 14">
      <path d="M6 13.2 C6 13.2 1.2 8.4 1.2 5.2 a4.8 4.8 0 0 1 9.6 0 C10.8 8.4 6 13.2 6 13.2 Z" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="6" cy="5.2" r="1.7" fill="currentColor" />
    </svg>
  </button>
);

export const QUEST_TRACKER_GUIDANCE_ID = 'quest-tracker-where-to-go';

interface QuestTrackerGuidancePanelProps {
  questId: string;
  questLog?: QuestLog;
  playerClass?: string;
  /** Anchor in the tracker's containing block (px): the panel docks to its left. */
  anchor: { top: number; right: number };
  zIndex: number;
  /** `byPointer`: closed by a mouse or touch click rather than the keyboard or Esc. */
  onClose: (byPointer: boolean) => void;
}

/**
 * The quest card's own "where to go" block (<QuestGuidance>), opened from the
 * tracker beside it. The player's position is read when it opens, like the
 * quest window's.
 */
export const QuestTrackerGuidancePanel: React.FC<QuestTrackerGuidancePanelProps> = ({
  questId, questLog, playerClass, anchor, zIndex, onClose,
}) => {
  const [you] = useState(readPlayerWhereabouts);
  // While drawn, Esc closes this panel before anything else (useGlobalInput).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    questTrackerGuidanceEscape.set(() => onCloseRef.current(false));
    return () => questTrackerGuidanceEscape.set(null);
  }, []);
  const quest = questCardView(questId, questLog, playerClass);
  if (!quest?.guidance) return null;
  return <section id={QUEST_TRACKER_GUIDANCE_ID} className="quest-tracker-guidance" role="region" aria-label={`Where to go: ${quest.title}`}
    data-testid={`quest-tracker-guidance-${questId}`}
    style={{
      ...npcPanel(), position: 'absolute', top: anchor.top, right: anchor.right, zIndex, pointerEvents: 'auto',
      width: `min(400px, calc(100vw - ${anchor.right + 16}px))`, maxHeight: `calc(100dvh - ${anchor.top + 12}px)`,
      overflowY: 'auto', padding: '10px 12px 0',
    }}>
    <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
      <div style={{ minWidth: 0 }}>
        <span className="npc-card-kicker">Where to go</span>
        <strong style={{ display: 'block', font: '600 15px/1.3 var(--player-display-font)', color: 'var(--player-text)' }}>{quest.title}</strong>
      </div>
      <button type="button" aria-label="Close where to go" onClick={e => onClose(e.detail > 0)}
        style={{ border: 0, background: 'transparent', color: COLORS.textMuted, cursor: 'pointer', fontSize: FONT_SIZE.sm, width: 28, height: 28, padding: 5 }}>
        ✕
      </button>
    </header>
    <QuestGuidance quest={quest} you={you} />
    <QuestTrackerAtlasLinks quest={quest} />
  </section>;
};
